import { Layers, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { useNavigationContext } from '@/shared/navigation/react/useNavigationContext';
import { cn } from '@/shared/lib/utils';
import { toggleJobsOverviewWindow } from '../internal/jobsWindows';
import { useJobsStore } from '../jobsStore';

/**
 * Indikátor operací na pozadí v horní liště (design varianta „orbit": 46×22, glyf vrstev,
 * při aktivitě spinning ring + rohový badge s počtem běžících jobů, při chybě danger
 * trojúhelník). Klik přepíná centrální okno „Operace na pozadí".
 */
export function OpsIndicator() {
  const { t } = useTranslation('jobs');
  const { kernel } = useNavigationContext();
  const { activeCount, spinning, anyFailed } = useJobsStore(
    useShallow((s) => {
      let activeCount = 0;
      let spinning = false;
      let anyFailed = false;
      for (const id of s.order) {
        const entry = s.runs[id];
        if (!entry) continue;
        if (entry.summary.status === 'Running') {
          activeCount += 1;
          if (entry.summary.counts.running > 0 || entry.summary.counts.ready > 0) spinning = true;
        }
        if (entry.summary.status === 'Failed') anyFailed = true;
      }
      return { activeCount, spinning, anyFailed };
    })
  );

  const active = activeCount > 0;
  const title = active
    ? t('indicator.activeTitle', { count: activeCount })
    : t('indicator.idleTitle');

  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      data-testid="ops-indicator"
      onClick={() => toggleJobsOverviewWindow(kernel)}
      className={cn(
        'bg-phx-surface border-phx-border-2 rounded-phx-md relative inline-flex h-[22px] w-[46px]',
        'shrink-0 cursor-pointer items-center justify-center border p-0'
      )}
    >
      <span className="relative inline-flex h-[14px] w-[14px] items-center justify-center">
        {spinning ? (
          <span
            className="border-t-phx-primary border-r-phx-primary absolute -inset-[2px] animate-spin rounded-full border-2 border-transparent [animation-duration:.9s]"
            aria-hidden
          />
        ) : null}
        {anyFailed ? (
          <TriangleAlert size={12} className="text-phx-danger" aria-hidden />
        ) : (
          <Layers
            size={12}
            className={cn(
              'transition-colors duration-200',
              active ? 'text-phx-primary' : 'text-phx-text-2'
            )}
            aria-hidden
          />
        )}
      </span>
      {active ? (
        <span
          className={cn(
            'bg-phx-primary text-phx-primary-fg ring-phx-surface absolute -top-[5px] -right-[5px]',
            'inline-flex h-4 min-w-4 items-center justify-center rounded-lg px-1',
            'text-[10px] font-bold tabular-nums ring-2'
          )}
        >
          {activeCount}
        </span>
      ) : null}
    </button>
  );
}
